import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { usePreloadTrialLockoutBackground } from '../hooks/usePreloadTrialLockoutBackground';

export type TrialLockoutContextValue = {
  isLockedOut: boolean;
  markSessionQuotaExhausted: () => void;
};

const TrialLockoutContext = createContext<TrialLockoutContextValue | null>(
  null
);

export type TrialLockoutProviderProps = {
  children: ReactNode;
  isTrialUser: () => boolean;
  renderLockoutModal: () => ReactNode;
};

export const TrialLockoutProvider = ({
  children,
  isTrialUser,
  renderLockoutModal,
}: TrialLockoutProviderProps) => {
  const isTrial = isTrialUser();
  const [isSessionQuotaExhausted, setIsSessionQuotaExhausted] = useState(false);
  const isBackgroundReady = usePreloadTrialLockoutBackground({
    enabled: isTrial,
  });

  const isLockedOut = isTrial && isSessionQuotaExhausted;

  const markSessionQuotaExhausted = useCallback(() => {
    setIsSessionQuotaExhausted(true);
  }, []);

  const value = useMemo(
    () => ({
      isLockedOut,
      markSessionQuotaExhausted,
    }),
    [isLockedOut, markSessionQuotaExhausted]
  );

  return (
    <TrialLockoutContext.Provider value={value}>
      {children}
      {isLockedOut && isBackgroundReady ? renderLockoutModal() : null}
    </TrialLockoutContext.Provider>
  );
};

export const useTrialLockout = (): TrialLockoutContextValue => {
  const context = useContext(TrialLockoutContext);

  if (!context) {
    throw new Error(
      'useTrialLockout must be used within a TrialLockoutProvider'
    );
  }

  return context;
};
